import { darken, getContrastRatio, lighten, type Theme } from '@mui/material/styles';
import { ThemeType } from 'src/components/utils/theme/ThemeType';

export const positionInputLabelAboveField: Record<string, string> = {
    position: 'relative',
    transform: ` translate(0px, 4px) scale(0.85) !important`,
    paddingBottom: '1px',
};

/**
 * sx-function for input labels positioned above the field. Applies the
 * existing `positionInputLabelAboveField` transform and, in light mode only,
 * forces the label color to `palette.secondary.main`.
 *
 * Usage: `<InputLabel sx={TextFieldStyleUtils.inputLabelAboveFieldSx}>`
 *
 * In dark mode the color override is intentionally omitted so the theme's
 * `MuiInputLabel.root.color` default (driven by `inputFieldLabelColor`) takes
 * effect — otherwise labels render dark-on-dark when tenants set a
 * brand-accent `secondaryColor`. Light mode stays byte-identical to the
 * pre-dark-mode behavior for existing tenants.
 */
export function inputLabelAboveFieldSx(theme: Theme) {
    return {
        ...positionInputLabelAboveField,
        ...(theme.palette.mode !== 'dark' && { color: theme.palette.secondary.main }),
    };
}

/**
 * sx-function for the *value text* inside timeslots-style inputs (the things
 * the user types). Light mode keeps the existing `secondary.main` tint —
 * byte-identical for existing tenants whose secondaryColor is a readable
 * dark brand tone on white inputs. Dark mode falls back to `text.primary`
 * (driven by widgetTextColor) so the typed value reads on the dark surface
 * instead of disappearing into a dark `secondary.main`.
 *
 * Usage: `<OutlinedInput inputProps={{ sx: TextFieldStyleUtils.timeslotsInputValueColorSx }} />`
 */
export function timeslotsInputValueColorSx(theme: Theme) {
    return {
        color:
            theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : theme.palette.secondary.main,
    };
}

export const positionInputLabelBelowField: Record<string, string> = {
    position: 'relative',
    transform: ` scale(0.85) !important`,
    paddingBottom: '1px',
    textAlign: 'center',
    transformOrigin: 'center center',
    maxWidth: '100%',
};

export function bookingWidgetInputStyle(
    customizations: ThemeType,
    backgroundColor?: string,
    color?: string,
) {
    // Explicit tenant override path (SB-943): when no caller-supplied color
    // context is in play AND the tenant has set bookingWidgetInputColor,
    // use it as the input background directly instead of running the
    // contrast heuristic below — which otherwise forces input backgrounds
    // to white whenever bookingWidgetColorContrast is darker than
    // bookingWidgetColor against white. Gated on `bookingWidgetInputColor`
    // specifically (not also on `bookingWidgetInputTextColor`) because the
    // text color was historically read by Dropdown/BookingWidgetCard only,
    // never by this function — tenants who set only the text color expect
    // the existing heuristic for the bg and would regress otherwise.
    // Callers that pass their own backgroundColor/color (ProductCardLandscape,
    // RequestTour, calendar props, etc.) are repurposing this util for a
    // non-widget surface and must continue through the heuristic.
    const callerProvidedColors = backgroundColor !== undefined || color !== undefined;
    if (!callerProvidedColors && customizations.bookingWidgetInputColor) {
        const isDark = customizations.darkMode === true;
        const bg = customizations.bookingWidgetInputColor;
        const fg =
            customizations.bookingWidgetInputTextColor ?? customizations.bookingWidgetColorContrast;
        // Dark mode lightens for hover affordance (darken on an already-dark
        // surface vanishes); mirrors the convention in Theme.ts MuiToggleButton.
        const hoverBg = isDark ? lighten(bg, 0.1) : darken(bg, 0.1);
        return {
            backgroundColor: `${bg} !important`,
            color: `${fg} !important`,
            border: 'none',
            transition: '0.1s ease-in-out all',
            '&:hover,&:focus,&:active': {
                backgroundColor: `${hoverBg} !important`,
                color: `${fg} !important`,
            },
            '&:focus': {
                boxShadow: `0 0 0 2px ${customizations.bookingWidgetColor}, \
                            0 0 0 3px ${customizations.bookingWidgetColorContrast}`,
            },
            '&.Mui-focused': {
                boxShadow: `0 0 0 2px ${customizations.bookingWidgetColor}, \
                            0 0 0 3px ${customizations.bookingWidgetColorContrast}`,
            },
        };
    }

    const useDarken =
        getContrastRatio('#ffffff', backgroundColor ?? customizations.bookingWidgetColor) >
        getContrastRatio('#ffffff', color ?? customizations.bookingWidgetColorContrast);
    const modifier = useDarken ? (color: string) => darken(color, 0.25) : () => '#FFF';
    return {
        backgroundColor: `${modifier(
            backgroundColor ?? customizations.bookingWidgetColor,
        )} !important`,
        color: `${color ?? customizations.bookingWidgetColorContrast} !important`,
        border: 'none',
        transition: '0.1s ease-in-out all',
        '&:hover,&:focus,&:active': {
            backgroundColor: `${modifier(
                backgroundColor ?? customizations.bookingWidgetColor,
            )} !important`,
            color: `${color ?? customizations.bookingWidgetColorContrast} !important`,
        },
        '&:focus': {
            boxShadow: `0 0 0 2px ${customizations.bookingWidgetColor}, \
                        0 0 0 3px ${customizations.bookingWidgetColorContrast}`,
        },
        '&.Mui-focused': {
            boxShadow: `0 0 0 2px ${customizations.bookingWidgetColor}, \
                        0 0 0 3px ${customizations.bookingWidgetColorContrast}`,
        },
    };
}

export function bookingSearchFormInputStyle(customizations: ThemeType): Record<string, any> {
    return {
        backgroundColor: `${customizations.bookingSearchFormInputColor} !important`,
        color: `${customizations.bookingSearchFormInputTextColor} !important`,
        border: 'none',
        transition: '0.1s ease-in-out all',
        '&:hover,&:active': {
            backgroundColor: `${darken(
                customizations.bookingSearchFormInputColor,
                0.1,
            )} !important`,
            color: `${customizations.bookingSearchFormInputTextColor} !important`,
        },
        '&.Mui-focused': {
            boxShadow: `0 0 0 2px ${customizations.bookingSearchFormColor}, \
                        0 0 0 3px ${customizations.bookingSearchFormColorContrast} !important`,
        },
    };
}

export default {
    positionInputLabelAboveField,
    positionInputLabelBelowField,
    inputLabelAboveFieldSx,
    timeslotsInputValueColorSx,
    bookingWidgetInputStyle,
    bookingSearchFormInputStyle,
};
